import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Cpu,
  Wifi,
  ShoppingBag,
  Receipt,
  MessageCircle,
  Wrench,
  Monitor,
  Printer,
  Scan,
  CreditCard,
  Sparkles,
} from "lucide-react";
import { PRODUCTS, ASSETS } from "../lib/brand";
import Reveal from "../components/Reveal";
import CountUp from "../components/CountUp";
import CTAStrip from "../components/CTAStrip";
import CustomerMarquee from "../components/CustomerMarquee";

const STATS = [
  { num: 3000, suffix: "+", label: "Businesses Served" },
  { num: 12, suffix: "+", label: "Years of Experience" },
  { num: 50, suffix: "+", label: "Industry Verticals" },
];

const USPS = [
  { icon: Cpu, title: "In-house Built", text: "Not resold. DigiPOS is entirely developed by Gravityinc engineers." },
  { icon: Wifi, title: "Offline + Online", text: "Works without internet. Syncs to cloud automatically when connected." },
  { icon: ShoppingBag, title: "Complete Bundle", text: "Software + hardware + support — everything from one vendor." },
  { icon: Receipt, title: "GST & Compliance Ready", text: "Auto E-Invoice, E-Way Bill and seamless Tally integration." },
  { icon: MessageCircle, title: "WhatsApp Billing", text: "Send digital bills and promotions to customers directly." },
  { icon: Wrench, title: "After-Sales Support", text: "Professional implementation, training and ongoing assistance." },
];

const INDUSTRIES = [
  "Supermarkets", "Hypermarkets", "Grocery Stores", "Textiles", "Footwear",
  "Pharmacies", "Mobile Shops", "Hardware Stores", "Restaurants", "Wholesale",
  "Distribution", "Amusement Parks", "Bakeries", "Jewellery", "Cafés", "Cosmetics",
];

const TESTIMONIALS = [
  {
    quote: "A great POS system is not just about software, it's about support. DigiPOS gave us both. Managing our 7 outlets has become easier, faster, and more reliable.",
    name: "Mr. Harris",
    biz: "Jeans House, Kochi",
  },
  {
    quote: "A modern retail business needs a smart solution. DigiPOS Zen by Gravityinc has given us complete control over our online and offline sales across all 4 outlets with ease, accuracy, and dependable support. We highly recommend it to every growing retailer.",
    name: "Mr. Mibinas ",
    biz: "Owner, Western Lady",
  },
  {
    quote: "Our journey from creating unique collections to delivering a better shopping experience became easier with Gravityinc’s DigiPOS solutions. Their reliable billing software and barcode printers have added efficiency and professionalism to our business. We are extremely happy with their products and support.",
    name: "Mr. Aby",
    biz: "Ariyaa",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative gi-hero-gradient text-white pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 gi-mesh opacity-50 pointer-events-none" />
        <div className="absolute -top-20 -right-20 h-[420px] w-[420px] rounded-full bg-[#00B4D8]/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 h-[360px] w-[360px] rounded-full bg-[#1565C0]/30 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <span className="gi-pill-dark mb-5">
                <Sparkles className="h-3.5 w-3.5" /> Trusted by 3,000+ Indian Businesses
              </span>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.15] tracking-[0.02em]">
                Complete Retail <br />
                & Distribution<br />
                <span className="bg-gradient-to-r from-white via-[#E6F8FD] to-[#00B4D8] bg-clip-text text-transparent">
                  System
                </span>
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-5 text-white/75 text-lg max-w-xl">
                DigiPOS is your all-in-one billing, inventory and business management platform — built in-house by Gravityinc, in Kochi.
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/contact" data-testid="hero-demo-btn" className="gi-btn-primary">
                  Request a Demo <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/digipos" data-testid="hero-explore-btn" className="gi-btn-ghost">
                  Explore Products
                </Link>
              </div>
            </Reveal>
            <Reveal delay={320}>
              <div className="mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm text-white/80">
                {["3,000+ Businesses", "12+ Years Experience", "In-house Built Software"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-2">
                    <Check className="h-4 w-4 text-[#00B4D8]" /> {t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <Reveal delay={120}>
              <div className="relative gi-float">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#00B4D8]/40 to-transparent blur-3xl rounded-3xl" />
                <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.35)] bg-white/5 backdrop-blur">
                  <img src={ASSETS.home} alt="DigiPOS Zen" className="w-full h-auto block" />
                </div>
                <div className="absolute -bottom-4 -left-4 bg-white text-[#1A1A2E] rounded-xl px-4 py-3 shadow-lg flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-[#10B981]/15 grid place-items-center text-[#10B981]">
                    <Check className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-xs text-[#6B7280]">GST Compliant</div>
                    <div className="text-sm font-semibold">E-Invoice & E-Way Bill</div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="bg-[#F5F8FF] gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-widest text-[#1565C0]">The Suite</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl mt-2 max-w-3xl">
              One Platform. <span className="text-[#1565C0]">Five</span> Powerful Solutions.
            </h2>
            <p className="text-[#6B7280] mt-4 max-w-2xl">
              Whether you run a single grocery store or a multi-branch distribution network, DigiPOS has a solution built for you.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.slug} delay={i * 80}>
                <Link
                  to={`/digipos/${p.slug}`}
                  data-testid={`home-product-${p.slug}`}
                  className="gi-card gi-card-hover p-7 block h-full relative overflow-hidden group"
                  style={{ borderTop: `4px solid ${p.accent}` }}
                >
                  <div
                    className="h-12 w-12 rounded-xl grid place-items-center mb-5"
                    style={{ background: `${p.accent}15`, color: p.accent }}
                  >
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <h3 className="font-display font-semibold text-xl mb-1">{p.name}</h3>
                  <p className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-3">{p.tag}</p>
                  <p className="text-sm text-[#1A1A2E]/80 mb-5">{p.oneLiner}</p>
                  <span
                    className="inline-flex items-center gap-1.5 text-sm font-medium group-hover:gap-2.5 transition-all"
                    style={{ color: p.accent }}
                  >
                    Learn More <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOMER LOGOS MARQUEE */}
      <CustomerMarquee />

      {/* TRUST */}
      <section className="bg-white gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STATS.map((s) => (
              <Reveal key={s.label}>
                <div className="gi-card p-8 text-center">
                  <div className="font-display font-bold text-5xl bg-gradient-to-r from-[#0B2545] via-[#1565C0] to-[#00B4D8] bg-clip-text text-transparent">
                    <CountUp end={s.num} suffix={s.suffix} />
                  </div>
                  <div className="text-[#6B7280] mt-2 text-sm">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <h3 className="font-display font-semibold text-2xl md:text-3xl text-center mt-16 mb-6">
              Powering businesses across every industry
            </h3>
          </Reveal>
          <Reveal>
            <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
              {INDUSTRIES.map((ind) => (
                <span
                  key={ind}
                  className="px-4 py-1.5 text-sm rounded-full border border-[#DDE6F0] bg-[#F5F8FF] text-[#1A1A2E] hover:border-[#1565C0] hover:text-[#1565C0] transition"
                >
                  {ind}
                </span>
              ))}
              <span className="px-4 py-1.5 text-sm text-[#6B7280] italic">and more…</span>
            </div>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 80}>
                <div
                  className="gi-card gi-card-hover p-7 h-full border border-[#E5EDF6] shadow-[0_22px_64px_rgba(11,37,69,0.20)] hover:shadow-[0_28px_72px_rgba(11,37,69,0.28)] hover:scale-[1.015] transition-all duration-300"
                  style={{ boxShadow: "0 22px 64px rgba(11, 37, 69, 0.20), 0 0 0 1px rgba(221, 230, 240, 0.9), 0 10px 24px rgba(0, 180, 216, 0.08)" }}
                >
                  <div className="text-[#00B4D8] text-5xl leading-none font-display">{`"`}</div>
                  <p className="text-[#1A1A2E]/85 text-base leading-relaxed mt-2">{t.quote}</p>
                  <div className="mt-6 pt-4 border-t border-[#DDE6F0]">
                    <div className="font-semibold text-sm">{t.name}</div>
                    <div className="text-xs text-[#6B7280]">{t.biz}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="bg-[#F5F8FF] gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-widest text-[#1565C0]">Why DigiPos</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl mt-2 max-w-3xl">
              Why thousands of businesses choose us
            </h2>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {USPS.map((u, i) => (
              <Reveal key={u.title} delay={i * 60}>
                <div className="gi-card gi-card-hover p-7 h-full">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#1565C0] to-[#00B4D8] grid place-items-center text-white mb-4">
                    <u.icon className="h-5 w-5" />
                  </div>
                  <h4 className="font-display font-semibold text-lg mb-2">{u.title}</h4>
                  <p className="text-sm text-[#6B7280] leading-relaxed">{u.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HARDWARE HIGHLIGHT */}
      <section className="gi-deep-gradient text-white gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <span className="gi-pill-dark mb-4">Hardware Ecosystem</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl leading-tight">
              Complete POS Hardware Solutions
            </h2>
            <p className="text-white/70 mt-4 max-w-lg">
              We supply, install and support a full range of POS hardware to complement your DigiPOS setup — bundled, tested, and ready to roll.
            </p>
            <Link to="/hardware" data-testid="home-hardware-cta" className="gi-btn-primary mt-7">
              View Hardware <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Monitor, label: "Touch POS" },
              { icon: Printer, label: "Thermal Printers" },
              { icon: Scan, label: "Barcode Scanners" },
              { icon: CreditCard, label: "RFID / Smart Cards" },
            ].map((h, i) => (
              <Reveal key={h.label} delay={i * 80}>
                <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-6 hover:bg-white/10 transition">
                  <div className="h-11 w-11 rounded-lg bg-[#00B4D8]/15 grid place-items-center text-[#00B4D8] mb-4">
                    <h.icon className="h-5 w-5" />
                  </div>
                  <div className="font-display font-semibold">{h.label}</div>
                  <div className="text-xs text-white/60 mt-1"></div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTAStrip />
    </div>
  );
}
